import { auth, db, serverTimestamp } from '@/firebase'
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
} from 'firebase/firestore'

export const EMPLOYEE_PORTAL_STATUSES = Object.freeze({
  VERIFICATION_REQUIRED: 'verification_required',
  FORM_REQUIRED: 'form_required',
  PENDING_APPROVAL: 'pending_approval',
  DENIED: 'denied',
  APPROVED: 'approved',
})

export const EMPLOYEE_PORTAL_ROUTE_NAMES = Object.freeze({
  [EMPLOYEE_PORTAL_STATUSES.VERIFICATION_REQUIRED]: 'EmployeeVerification',
  [EMPLOYEE_PORTAL_STATUSES.FORM_REQUIRED]: 'EmployeeFormStep',
  [EMPLOYEE_PORTAL_STATUSES.PENDING_APPROVAL]: 'EmployeePendingApproval',
  [EMPLOYEE_PORTAL_STATUSES.DENIED]: 'EmployeePendingApproval',
  [EMPLOYEE_PORTAL_STATUSES.APPROVED]: 'Dashboard',
})

export const MAX_EMPLOYEE_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024
export const CUSTOMER_PROFILE_REQUIRED_SERVICE_OPTIONS = Object.freeze([
  'ACGME Accreditation Guidance',
  'Scientific Writing',
  'Conference Preparation',
  'Poster & Presentation Design',
  'Grant Preparation',
  'Research Collaboration',
  'Statistical Services',
  'Project Management',
  'Personalized Mentorship',
  'Clinical & Basic Science Support',
  'IRB Submission Support',
  'Streamlined Research Documentation',
  'Webinar & Lecture Development',
  'Research Evaluation & Reporting',
  'Data Handling & Analysis',
  'Event Planning & Recognition',
])

const DEFAULT_EMPLOYEE_FORM_URL = 'https://forms.office.com/r/ytdmUWHwWr?origin=lprLink'
const CUSTOMER_PROFILE_DEFAULTS = Object.freeze({
  firstName: '',
  lastName: '',
  email: '',
  primaryContactNumber: '',
  isInternationalStudent: '',
  countryOfCitizenship: '',
  currentStatus: '',
  currentStatusOther: '',
  highestEducation: '',
  highestEducationOther: '',
  requestedServices: [],
  preferredSchedule: '',
  specialRequirementsOrPreferences: '',
  serviceTimeFrame: '',
  registrationDecision: '',
  completed: false,
  completedAt: null,
})

export function normalizeEmail(value) {
  const email = (value || '').trim().toLowerCase()
  return email || null
}

async function findSingleUserProfile(field, value) {
  if (!value) return null

  const usersRef = collection(db, 'users')
  const snap = await getDocs(query(usersRef, where(field, '==', value), limit(1)))
  if (snap.empty) return null

  const docSnap = snap.docs[0]
  return { ref: docSnap.ref, id: docSnap.id, data: docSnap.data(), exists: true }
}

export async function resolveUserProfile(user = auth.currentUser) {
  const uid = user?.uid || null
  const email = normalizeEmail(user?.email)

  const byUid = await findSingleUserProfile('uid', uid)
  if (byUid) return byUid

  const byEmail = await findSingleUserProfile('email', email)
  if (byEmail) return byEmail

  if (!uid) return null
  const ref = doc(db, 'users', uid)
  return { ref, id: ref.id, data: null, exists: false }
}

export async function mergeUserProfile(user, data) {
  const profile = await resolveUserProfile(user)
  if (!profile?.ref || !user) throw new Error('Unable to resolve the current user profile.')

  const payload = {
    updatedAt: serverTimestamp(),
    ...data,
  }

  if (!profile.exists) {
    payload.uid = user.uid || null
    payload.email = normalizeEmail(user.email)
    payload.name = user.displayName || null
    payload.role = 'User'
    payload.status = 'Active'
    payload.createdAt = serverTimestamp()
  } else {
    if (!profile.data?.uid && user.uid) payload.uid = user.uid
    const email = normalizeEmail(user.email)
    if (email && profile.data?.email !== email) payload.email = email
    if (!profile.data?.name && user.displayName) payload.name = user.displayName
  }

  await setDoc(profile.ref, payload, { merge: true })
  return profile.ref
}

export function getUserRoleFromProfile(profile) {
  const data = profile?.data || profile || {}
  if (data?.role === 'Owner') return 'Owner'
  if (data?.role === 'Admin') return 'Admin'
  return data?.employeePortal?.status === EMPLOYEE_PORTAL_STATUSES.APPROVED ? 'Admin' : 'User'
}

export function getUserDisplayName(profile, user = auth.currentUser) {
  const data = profile?.data || profile || {}
  return data?.name || user?.displayName || normalizeEmail(user?.email) || 'User'
}

export function getCustomerProfile(profile) {
  const data = profile?.data || profile || {}
  const profileData = data?.profile || {}
  return {
    ...CUSTOMER_PROFILE_DEFAULTS,
    ...profileData,
    requestedServices: Array.isArray(profileData.requestedServices) ? [...profileData.requestedServices] : [],
  }
}

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0
}

export function isCustomerProfileComplete(profile) {
  const customerProfile = getCustomerProfile(profile)

  if (!hasText(customerProfile.firstName)) return false
  if (!hasText(customerProfile.lastName)) return false
  if (!hasText(customerProfile.email)) return false
  if (!hasText(customerProfile.primaryContactNumber)) return false
  if (!['yes', 'no'].includes(customerProfile.isInternationalStudent)) return false
  if (!hasText(customerProfile.countryOfCitizenship)) return false
  if (!hasText(customerProfile.currentStatus)) return false
  if (customerProfile.currentStatus === 'other' && !hasText(customerProfile.currentStatusOther)) return false
  if (!hasText(customerProfile.highestEducation)) return false
  if (customerProfile.highestEducation === 'other' && !hasText(customerProfile.highestEducationOther)) return false
  if (!Array.isArray(customerProfile.requestedServices) || customerProfile.requestedServices.length === 0) return false
  if (!hasText(customerProfile.preferredSchedule)) return false
  if (!hasText(customerProfile.specialRequirementsOrPreferences)) return false
  if (!hasText(customerProfile.serviceTimeFrame)) return false
  if (customerProfile.registrationDecision !== 'agree') return false

  return customerProfile.completed === true || !!customerProfile.completedAt
}

export function buildCustomerProfileDraft(user = auth.currentUser, profile = null) {
  const email = normalizeEmail(user?.email)
  const customerProfile = getCustomerProfile(profile)
  const fullName = (user?.displayName || '').trim()
  const [firstName = '', ...rest] = fullName ? fullName.split(/\s+/) : []
  const lastName = rest.join(' ')

  return {
    ...customerProfile,
    email: customerProfile.email || email || '',
    firstName: customerProfile.firstName || firstName,
    lastName: customerProfile.lastName || lastName,
  }
}

export function hasEmployeePortalRecord(profile) {
  const data = profile?.data || profile || {}
  const employeePortal = data?.employeePortal
  return !!(employeePortal && Object.keys(employeePortal).length)
}

export function hasStartedEmployeeApplication(profile) {
  const data = profile?.data || profile || {}
  const employeePortal = data?.employeePortal || {}
  const status = employeePortal?.status

  if (status === EMPLOYEE_PORTAL_STATUSES.FORM_REQUIRED) return true
  if (status === EMPLOYEE_PORTAL_STATUSES.PENDING_APPROVAL) return true
  if (status === EMPLOYEE_PORTAL_STATUSES.DENIED) return true
  if (status === EMPLOYEE_PORTAL_STATUSES.APPROVED) return true
  if (employeePortal?.applicationRequestedAt) return true
  if (employeePortal?.verificationSubmittedAt) return true
  if (hasText(employeePortal?.selfiePath)) return true
  if (hasText(employeePortal?.idDocumentPath)) return true

  return false
}

export function getEmployeePortalStatus(profile) {
  const data = profile?.data || profile || {}
  const status = data?.employeePortal?.status
  return Object.values(EMPLOYEE_PORTAL_STATUSES).includes(status)
    ? status
    : EMPLOYEE_PORTAL_STATUSES.VERIFICATION_REQUIRED
}

export function getEmployeePortalRouteName(status) {
  return EMPLOYEE_PORTAL_ROUTE_NAMES[status] || EMPLOYEE_PORTAL_ROUTE_NAMES[EMPLOYEE_PORTAL_STATUSES.VERIFICATION_REQUIRED]
}

export async function getEmployeePortalAccess(user = auth.currentUser) {
  const profile = await resolveUserProfile(user)
  if (getUserRoleFromProfile(profile) === 'Owner') {
    return {
      profile,
      status: EMPLOYEE_PORTAL_STATUSES.APPROVED,
      hasRecord: true,
      hasStartedApplication: true,
      routeName: 'Dashboard',
    }
  }
  if (getUserRoleFromProfile(profile) === 'Admin') {
    return {
      profile,
      status: EMPLOYEE_PORTAL_STATUSES.APPROVED,
      hasRecord: hasEmployeePortalRecord(profile),
      hasStartedApplication: true,
      routeName: 'Dashboard',
    }
  }
  const hasRecord = hasEmployeePortalRecord(profile)
  const hasStartedApplication = hasStartedEmployeeApplication(profile)
  const status = hasStartedApplication ? getEmployeePortalStatus(profile) : null
  return {
    profile,
    status,
    hasRecord,
    hasStartedApplication,
    routeName: hasStartedApplication ? getEmployeePortalRouteName(status) : 'EmployeeApplicationPrompt',
  }
}

export function getEmployeeFormUrl() {
  return (process.env.VUE_APP_EMPLOYEE_FORM_URL || DEFAULT_EMPLOYEE_FORM_URL || '').trim()
}

export function sanitizeFileName(name) {
  return (name || 'upload')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'upload'
}

export function buildEmployeeVerificationStoragePath(uid, type, fileName) {
  const folder = type === 'selfie' ? 'selfie' : 'documents'
  return `employee-verification/${uid}/${folder}/${Date.now()}-${sanitizeFileName(fileName)}`
}

const EMPLOYEE_PROFILE_DEFAULTS = Object.freeze({
  firstName: '',
  lastName: '',
  email: '',
  primaryContactNumber: '',
  countryOfCitizenship: '',
  departments: [],
  departmentsOther: '',
  serviceCategories: [],
  serviceCategoriesOther: '',
  weeklyCapacity: '',
  rateType: '',
  rate: '',
  acknowledgment: '',
  status: 'draft',
  completedAt: null,
})

export function getEmployeeProfile(profile) {
  const data = profile?.data || profile || {}
  const profileData = data?.employeeProfile || {}
  return {
    ...EMPLOYEE_PROFILE_DEFAULTS,
    ...profileData,
    departments: Array.isArray(profileData.departments) ? [...profileData.departments] : [],
    serviceCategories: Array.isArray(profileData.serviceCategories) ? [...profileData.serviceCategories] : [],
  }
}

export function isEmployeeProfileComplete(profile) {
  const employeeProfile = getEmployeeProfile(profile)

  if (!hasText(employeeProfile.firstName)) return false
  if (!hasText(employeeProfile.lastName)) return false
  if (!hasText(employeeProfile.email)) return false
  if (!hasText(employeeProfile.primaryContactNumber)) return false
  if (!hasText(employeeProfile.countryOfCitizenship)) return false
  if (!Array.isArray(employeeProfile.departments) || employeeProfile.departments.length === 0) return false
  if (employeeProfile.departments.includes('Other') && !hasText(employeeProfile.departmentsOther)) return false
  if (!Array.isArray(employeeProfile.serviceCategories) || employeeProfile.serviceCategories.length === 0) return false
  if (employeeProfile.serviceCategories.includes('Other') && !hasText(employeeProfile.serviceCategoriesOther)) return false
  if (!hasText(employeeProfile.weeklyCapacity)) return false
  if (!hasText(employeeProfile.rateType)) return false
  if (!hasText(employeeProfile.rate)) return false
  if (employeeProfile.acknowledgment !== 'agree') return false

  return employeeProfile.status === 'completed' || !!employeeProfile.completedAt
}

export function buildEmployeeProfileDraft(user = auth.currentUser, profile = null) {
  const email = normalizeEmail(user?.email)
  const employeeProfile = getEmployeeProfile(profile)
  const fullName = (user?.displayName || '').trim()
  const [firstName = '', ...rest] = fullName ? fullName.split(/\s+/) : []
  const lastName = rest.join(' ')

  return {
    ...employeeProfile,
    email: employeeProfile.email || email || '',
    firstName: employeeProfile.firstName || firstName,
    lastName: employeeProfile.lastName || lastName,
  }
}
